/**
 * Ant Design 国际化配置组件
 * 根据当前语言动态切换 Ant Design 的 locale
 */
import React from 'react'
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import enUS from 'antd/locale/en_US'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import 'dayjs/locale/en'
import { useLanguage } from '@/hooks/useLanguage'
import { useTheme } from '@/hooks'
import GeekThemeProvider from '@/theme/geek'
import HeroThemeProvider from '@/theme/hero'
import CyberThemeProvider from '@/theme/cyber'
import TechThemeProvider from '@/theme/tech'
import BlueThemeProvider from '@/theme/blue'

interface AntdConfigProviderProps {
  children: React.ReactNode
}

const APP_HEADER_HEIGHT = 64
const APP_MESSAGE_GAP = 12
const APP_MESSAGE_TOP = APP_HEADER_HEIGHT + APP_MESSAGE_GAP

// Ant Design locale 映射
const antdLocaleMap = {
  'zh-CN': zhCN,
  'en-US': enUS,
}

// dayjs locale 映射
const dayjsLocaleMap = {
  'zh-CN': 'zh-cn',
  'en-US': 'en',
}

const AntdConfigProvider: React.FC<AntdConfigProviderProps> = ({ children }) => {
  const { currentLanguage } = useLanguage()
  const { resolvedTheme, isGeek, isHero, isCyber, isTech, isBlue, themeMode } = useTheme()
  const isDark = resolvedTheme === 'dark'

  // 设置 dayjs locale
  React.useEffect(() => {
    const dayjsLocale = dayjsLocaleMap[currentLanguage] || 'zh-cn'
    dayjs.locale(dayjsLocale)
  }, [currentLanguage])

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      const dataTheme = isGeek ? 'geek' : isHero ? themeMode : isCyber ? 'cyber' : isTech ? 'tech' : isBlue ? 'blue' : resolvedTheme
      document.documentElement.setAttribute('data-theme', dataTheme)
    }
  }, [resolvedTheme, isGeek, isHero, isCyber, isTech, isBlue, themeMode])

  // 获取 Ant Design locale
  const antdLocale = antdLocaleMap[currentLanguage] || zhCN

  // Hero 主题 token
  const heroTokens = isHero
    ? {
        colorPrimary: '#7c3aed',
        colorBgLayout: isDark ? '#18181b' : '#FAFAFA',
        colorBgContainer: isDark ? '#27272a' : '#FFFFFF',
        colorBorderSecondary: isDark ? '#3f3f46' : '#E4E4E7',
        colorTextBase: isDark ? '#FAFAFA' : '#18181b',
        colorTextSecondary: isDark ? '#A1A1AA' : '#71717A',
        borderRadius: 14,
      }
    : null

  // Cyber 主题 token
  const cyberTokens = isCyber
    ? {
        colorPrimary: '#00f0ff',
        colorBgLayout: '#0b1120',
        colorBgContainer: 'rgba(15, 23, 41, 0.85)',
        colorBorderSecondary: 'rgba(0, 240, 255, 0.15)',
        colorTextBase: '#e0f7fa',
        colorTextSecondary: '#7ec8cf',
        borderRadius: 4,
      }
    : null

  // Tech 主题 token
  const techTokens = isTech
    ? {
        colorPrimary: '#06b6d4',
        colorBgLayout: '#020617',
        colorBgContainer: '#0f172a',
        colorBorderSecondary: 'rgba(6, 182, 212, 0.15)',
        colorTextBase: '#e2e8f0',
        colorTextSecondary: '#94a3b8',
        borderRadius: 4,
      }
    : null

  // Blue 主题 token
  const blueTokens = isBlue
    ? {
        colorPrimary: '#2E81FF',
        colorBgBase: '#050B14',
        colorBgLayout: '#050B14',
        colorBgContainer: 'rgba(17, 29, 44, 0.8)',
        colorBorder: '#1E3A5F',
        colorBorderSecondary: '#1E3A5F',
        colorTextBase: '#e2e8f0',
        colorTextSecondary: '#94a3b8',
        borderRadius: 4,
        wireframe: false,
      }
    : null

  // Blue 主题组件级 token
  const blueComponentTokens = isBlue
    ? {
        Table: {
          colorBgContainer: 'transparent',
          headerBg: 'rgba(30, 58, 95, 0.3)',
          borderColor: '#1E3A5F',
          rowHoverBg: 'rgba(22, 119, 255, 0.15)',
        },
        Card: {
          colorBgContainer: 'rgba(13, 25, 48, 0.75)',
          actionsBg: 'transparent',
        },
        Tabs: {
          itemColor: '#94a3b8',
          itemSelectedColor: '#fff',
          inkBarColor: '#2E81FF',
        },
        Modal: {
          contentBg: '#0b1624',
          headerBg: '#0b1624',
        },
        Input: {
          colorBgContainer: 'rgba(0, 0, 0, 0.2)',
          activeBorderColor: '#2E81FF',
        },
      }
    : null

  // 默认 token
  const defaultTokens = {
    colorPrimary: '#2563EB',
    colorBgLayout: isDark ? '#0B1120' : '#F5F7FB',
    colorBgContainer: isDark ? '#0F172A' : '#FFFFFF',
    colorBorderSecondary: isDark ? '#1F2937' : '#E6EAF2',
    colorTextBase: isDark ? '#E5E7EB' : '#0F172A',
    colorTextSecondary: isDark ? '#9CA3AF' : '#475569',
    borderRadius: 12,
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: blueTokens || techTokens || cyberTokens || heroTokens || defaultTokens,
        ...(blueComponentTokens ? { components: blueComponentTokens } : {}),
      }}
    >
      <AntdApp message={{ maxCount: 1, top: APP_MESSAGE_TOP }}>
        <GeekThemeProvider disabled={!isGeek}>
          <HeroThemeProvider disabled={!isHero}>
            <CyberThemeProvider disabled={!isCyber}>
              <TechThemeProvider disabled={!isTech}>
                <BlueThemeProvider disabled={!isBlue}>
                  {children}
                </BlueThemeProvider>
              </TechThemeProvider>
            </CyberThemeProvider>
          </HeroThemeProvider>
        </GeekThemeProvider>
      </AntdApp>
    </ConfigProvider>
  )
}

export default AntdConfigProvider
